import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenantsAPI, OnboardingState } from '../api/tenants';
import './OnboardingChecklist.css';

interface StepDef {
  key: keyof OnboardingState['steps'];
  label: string;
  hint: string;
  path: string;
}

const STEPS: StepDef[] = [
  { key: 'profile', label: 'Fill in your company profile', hint: 'Address and phone — shown on your reports', path: '/admin/settings' },
  { key: 'logo', label: 'Upload your company logo', hint: 'Appears on your dashboard and PDF reports', path: '/admin/settings' },
  { key: 'signature', label: 'Add the engineer signature', hint: 'Used to sign approved PDF reports', path: '/admin/settings' },
  { key: 'technician', label: 'Add your first technician', hint: 'The field tech who will run tests on site', path: '/admin/technicians' },
  { key: 'project', label: 'Create your first project', hint: 'Projects hold work orders and reports', path: '/admin/create-project' },
];

/**
 * "Get set up" card for new tenant admins. Steps are computed server-side from
 * real data, so they check themselves off in any order across sessions.
 * Hidden once dismissed or when everything is done.
 */
const OnboardingChecklist: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    tenantsAPI.getOnboarding()
      .then(setState)
      .catch(() => setState(null)); // non-admin or older backend — show nothing
  }, []);

  if (!state || state.dismissed || hidden) return null;

  const doneCount = STEPS.filter((s) => state.steps[s.key]).length;
  if (doneCount === STEPS.length) return null;

  const dismiss = async () => {
    setHidden(true);
    try {
      await tenantsAPI.dismissOnboarding();
    } catch {
      // Hidden locally either way; will reappear next session if the call failed
    }
  };

  return (
    <div className="onboarding-card">
      <div className="onboarding-header">
        <div>
          <h2>Get set up</h2>
          <span className="onboarding-progress-text">{doneCount} of {STEPS.length} done</span>
        </div>
        <button className="onboarding-dismiss" onClick={dismiss} title="Hide this checklist permanently">
          Hide
        </button>
      </div>
      <div className="onboarding-progress-track">
        <div className="onboarding-progress-fill" style={{ width: `${(doneCount / STEPS.length) * 100}%` }} />
      </div>
      <ul className="onboarding-steps">
        {STEPS.map((step) => {
          const done = state.steps[step.key];
          return (
            <li key={step.key} className={done ? 'onboarding-step onboarding-step-done' : 'onboarding-step'}>
              <button className="onboarding-step-button" onClick={() => navigate(step.path)} disabled={done}>
                <span className="onboarding-check" aria-hidden="true">{done ? '✓' : ''}</span>
                <span className="onboarding-step-text">
                  <span className="onboarding-step-label">{step.label}</span>
                  {!done && <span className="onboarding-step-hint">{step.hint}</span>}
                </span>
                {!done && <span className="onboarding-step-go" aria-hidden="true">→</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OnboardingChecklist;
